import { useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Btn } from "../UI";
import { printHandout } from "./handoutPrint";
import { canRetryEmail, reminderToast, remindedRecently } from "./reminderShared";
import { Pencil, Trash2, CheckCircle2, Printer, Mail, Share2, BookPlus, Eye, BellRing, RotateCw } from "lucide-react";

const SOURCE = { ai: "AI draft", clinic: "Clinic handout", public: "Public links" };
const when = (d) => (d ? dayjs(d).format("MMM D, YYYY h:mm A") : "");

function EmailConfirm({ h, email, onDone, onCancel }) {
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/client-handouts/${h.id}/email`, { confirm_consent: true }); toast.success(`Emailed to ${email}`); onDone(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3" data-testid={`edu-email-form-${h.id}`}>
      {email ? <p className="text-xs text-[#5D4037]">Sends to <strong data-testid={`edu-email-to-${h.id}`}>{email}</strong>, the email on the client profile.</p>
        : <p className="text-xs font-semibold text-[#C62828]">This client has no email on file. Add one on the client profile first.</p>}
      <label className="flex items-start gap-2 text-xs text-[#5D4037]"><input type="checkbox" data-testid={`edu-email-consent-${h.id}`} checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4E342E]" />
        The client agreed to receive health information by email.</label>
      <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn data-testid={`edu-email-send-${h.id}`} disabled={!email || !ok || busy} onClick={send}><Mail size={14} /> {busy ? "Sending" : "Send email"}</Btn></div>
    </div>
  );
}

const unopenedDays = (h) => (h.shared_portal && !h.viewed_at && h.shared_at ? dayjs().diff(dayjs(h.shared_at), "day") : -1);

function ReminderAction({ h, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!h.shared_portal || h.viewed_at) return null;
  const go = async (retry) => {
    setBusy(true);
    try { const r = await api.post(`/client-handouts/${h.id}/remind${retry ? "/retry-email" : ""}`); reminderToast(r.data.last_reminder, retry); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (canRetryEmail(h)) return <Btn variant="outline" data-testid={`edu-retry-email-${h.id}`} disabled={busy} onClick={() => go(true)}><RotateCw size={13} /> {busy ? "Sending" : "Retry email"}</Btn>;
  const recent = remindedRecently(h);
  return (
    <Btn variant="outline" data-testid={`edu-remind-${h.id}`} disabled={busy || recent} onClick={() => go(false)}
      title={recent ? `A reminder went out ${when(h.last_reminder_at)}. You can send another after 24 hours.` : "Send a portal notice and an email"}>
      <BellRing size={13} /> {busy ? "Sending" : recent ? "Reminder sent today" : "Send reminder"}</Btn>
  );
}

function Activity({ h }) {
  const bits = [h.prints?.length ? `Printed ${h.prints.length}x` : "", h.emails?.length ? `Emailed ${h.emails.length}x, last ${when(h.emails[h.emails.length - 1].at)}` : "",
    h.reminders?.length ? `Reminded ${h.reminders.length}x, last ${when(h.reminders[h.reminders.length - 1].at)}` : ""].filter(Boolean);
  const days = unopenedDays(h);
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#7B6157]">
      {bits.map((b) => <span key={b}>{b}</span>)}
      {h.shared_portal && <span className="font-semibold text-[#2E7D32]" data-testid={`edu-portal-state-${h.id}`}>In the portal{h.viewed_at ? `, opened ${when(h.viewed_at)}` : ", not opened yet"}</span>}
      {days >= 7 && <span data-testid={`edu-unopened-${h.id}`} className="rounded-full bg-[#FFF3E0] px-2 py-0.5 font-bold text-[#8D5A00]">Not opened after {days} days</span>}
    </div>
  );
}

/** One handout given for a diagnosis, with its approval state and delivery actions. */
export function HandoutCard({ h, client, editable, isAdmin, onEdit, onChanged }) {
  const [emailing, setEmailing] = useState(false);
  const draft = h.status === "draft";
  const run = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const print = () => { printHandout(h, `${client.first_name} ${client.last_name}`); run(() => api.post(`/client-handouts/${h.id}/printed`)); };
  return (
    <div className="rounded-2xl border border-[#EFEBE0] bg-white p-4" data-testid={`edu-handout-${h.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-[#3E2723]">{h.title}</span>
        <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-semibold text-[#6D4C41]">{SOURCE[h.source]}</span>
        <span data-testid={`edu-status-${h.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${draft ? "bg-[#FFF3E0] text-[#8D5A00]" : "bg-[#E8F5E9] text-[#2E7D32]"}`}>{draft ? "Draft, not approved" : "Approved"}</span>
      </div>
      <div className="mt-0.5 text-[11px] text-[#7B6157]">{draft ? `Drafted by ${h.drafted_by_name} on ${when(h.created_at)}` : `Approved by ${h.approved_by_name} on ${when(h.approved_at)}`}</div>
      <Activity h={h} />
      {editable && <div className="mt-3 flex flex-wrap gap-2">
        {draft ? <>
          <Btn variant="outline" data-testid={`edu-edit-${h.id}`} onClick={() => onEdit(h)}><Pencil size={13} /> Review and approve</Btn>
          <Btn variant="ghost" data-testid={`edu-delete-${h.id}`} onClick={() => run(() => api.delete(`/client-handouts/${h.id}`), "Draft deleted")} className="!text-[#C62828]"><Trash2 size={13} /> Delete draft</Btn>
        </> : <>
          <Btn variant="outline" data-testid={`edu-print-${h.id}`} onClick={print}><Printer size={13} /> Print</Btn>
          <Btn variant="outline" data-testid={`edu-email-${h.id}`} onClick={() => setEmailing(true)}><Mail size={13} /> Email</Btn>
          <Btn variant="outline" data-testid={`edu-portal-${h.id}`} onClick={() => run(() => api.post(`/client-handouts/${h.id}/portal`, { shared: !h.shared_portal }), h.shared_portal ? "Removed from the portal" : "Shared to the Client Portal")}>
            {h.shared_portal ? <Eye size={13} /> : <Share2 size={13} />} {h.shared_portal ? "Stop sharing" : "Share to portal"}</Btn>
          <ReminderAction h={h} onChanged={onChanged} />
          {isAdmin && h.source !== "clinic" && <Btn variant="ghost" data-testid={`edu-save-lib-${h.id}`} onClick={() => run(() => api.post(`/client-handouts/${h.id}/save-to-library`), "Saved to the clinic library")}><BookPlus size={13} /> Save to clinic library</Btn>}
        </>}
      </div>}
      {!draft && !editable && <div className="mt-3"><Btn variant="outline" data-testid={`edu-print-${h.id}`} onClick={() => printHandout(h, `${client.first_name} ${client.last_name}`)}><Printer size={13} /> Print</Btn></div>}
      {emailing && <EmailConfirm h={h} email={client.email} onCancel={() => setEmailing(false)} onDone={() => { setEmailing(false); onChanged(); }} />}
      {draft && <p className="mt-2 flex items-center gap-1 text-[11px] text-[#8D5A00]"><CheckCircle2 size={11} /> Print, email, and portal sharing unlock after approval.</p>}
    </div>
  );
}
